import { Controller } from "@hotwired/stimulus"

// Bulk select on the Messages list. Hovering a row's avatar turns it into a
// checkbox (tapping it does the same on touch); ticking one starts selecting,
// and the filter toolbar gives way to a bar: a select-all checkbox in the
// avatar column (a dash while only some are ticked), then Archive and Delete
// with the count each applies to. Unticking the last one, or Esc, ends it.
//
// Emails, Jane conversations, channel chats and person rows are selectable.
// Each box carries its kind and its own archive (and, for email, trash) url -
// the same endpoints the row swipe, card stack and side panel use. Jane
// conversations delete for good in one DELETE /assistant/conversations
// (Conversations::Purge). Chats and person rows are archive-only
// (Messages::Archive): with one of them ticked, Delete is off.
// While selecting, a click anywhere on a row ticks it instead of opening it.
//
// The Gmail habits: Shift-click ticks (or unticks) every row between the last
// one you ticked and this one. On the keyboard, x ticks the row j/k has
// highlighted (thread_triage_controller), Shift+x ticks the range up to it,
// and while selecting e archives and # deletes.
const ARCHIVE_ONLY = ["chat", "person"]

export default class extends Controller {
  static targets = ["all", "archive", "archiveCount", "remove", "removeCount", "status"]
  static values = { url: String }

  connect() {
    this._onRowClick = (event) => this._rowClick(event)
    this._onKeydown = (event) => this._keydown(event)
    // A Shift-click would otherwise also select the text between the clicks.
    this._onMousedown = (event) => {
      if (event.shiftKey && (this._selecting() || event.target.closest(".msg-select"))) event.preventDefault()
    }
    this.element.addEventListener("click", this._onRowClick, true)
    this.element.addEventListener("mousedown", this._onMousedown)
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("click", this._onRowClick, true)
    this.element.removeEventListener("mousedown", this._onMousedown)
    document.removeEventListener("keydown", this._onKeydown)
    this._setSelecting(false)
  }

  clear() {
    this._boxes().forEach((box) => { box.checked = false })
    this.changed()
  }

  // The bar's checkbox: anything short of all ticks the lot; all clears it.
  toggleAll() {
    const boxes = this._boxes()
    const all = boxes.every((box) => box.checked)
    boxes.forEach((box) => { box.checked = !all })
    this.changed()
  }

  changed() {
    const count = this._chosen().length
    const total = this._boxes().length
    this._setSelecting(count > 0)
    this.allTarget.checked = count > 0 && count === total
    this.allTarget.indeterminate = count > 0 && count < total
    this.statusTarget.textContent = ""
    this.archiveTarget.disabled = count === 0
    this.archiveCountTarget.textContent = count || ""
    const archiveOnly = this._chosen().some((box) => ARCHIVE_ONLY.includes(box.dataset.kind))
    this.removeTarget.disabled = count === 0 || archiveOnly
    this.removeCountTarget.textContent = archiveOnly ? "" : (count || "")
    this.removeTarget.title = archiveOnly ? "Chats can be archived, not deleted" : ""
  }

  async archive() {
    const boxes = this._chosen()
    if (!boxes.length) return
    await this._run(boxes, () => this._each(boxes, (box) => this._post(box.dataset.archiveUrl)), "Couldn't archive some of those - try again.")
  }

  async remove() {
    const boxes = this._chosen()
    if (!boxes.length || boxes.some((box) => ARCHIVE_ONLY.includes(box.dataset.kind))) return
    const emails = boxes.filter((box) => box.dataset.kind === "email")
    const chats = boxes.filter((box) => box.dataset.kind === "assistant")
    const noun = boxes.length === 1 ? "this message" : `these ${boxes.length} messages`
    const notes = []
    if (emails.length) notes.push(emails.length === 1 ? "The email goes to Gmail's trash." : "Emails go to Gmail's trash.")
    if (chats.length) notes.push(chats.length === 1 ? "The Jane conversation is deleted for good." : "Jane conversations are deleted for good.")
    if (!window.confirm(`Delete ${noun}? ${notes.join(" ")}`)) return
    await this._run(boxes, async () => {
      const done = await this._each(emails, (box) => this._post(box.dataset.trashUrl))
      if (chats.length) {
        const ok = await this._request(this.urlValue, "DELETE", { ids: chats.map((box) => box.value) })
        if (ok) done.push(...chats)
      }
      return done
    }, "Couldn't delete some of those - try again.")
  }

  // Runs a bulk action with the bar's buttons locked, then drops the rows it
  // finished. Rows that failed stay ticked so a second press retries them.
  async _run(boxes, action, failure) {
    this.archiveTarget.disabled = true
    this.removeTarget.disabled = true
    const done = await action()
    done.forEach((box) => box.closest(".msg-swipe")?.remove())
    this.changed()
    if (done.length < boxes.length) this.statusTarget.textContent = failure
  }

  async _each(boxes, call) {
    const results = await Promise.all(boxes.map(async (box) => ((await call(box)) ? box : null)))
    return results.filter(Boolean)
  }

  _post(url) {
    return url ? this._request(url, "POST") : Promise.resolve(false)
  }

  async _request(url, method, body) {
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
        credentials: "same-origin",
        body: body ? JSON.stringify(body) : undefined
      })
      return response.ok
    } catch (_error) {
      return false
    }
  }

  _keydown(event) {
    if (event.key === "Escape") {
      if (this._selecting()) this.clear()
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return
    const target = event.target
    if (target?.matches?.("input:not([type=checkbox]), textarea, select") || target?.isContentEditable) return
    // The card stack owns the keyboard while it's open.
    if (this.application.getControllerForElementAndIdentifier(this.element, "card-stack")?.isOpen?.()) return

    if (event.key === "x" || event.key === "X") {
      const box = this._boxFor(this.element.querySelector(".assistant-thread-item.is-selected"))
      if (!box) return
      event.preventDefault()
      this._tick(box, !box.checked, event.shiftKey)
    } else if (event.key === "e" && this._selecting() && !this.archiveTarget.disabled) {
      event.preventDefault()
      this.archive()
    } else if (event.key === "#" && this._selecting() && !this.removeTarget.disabled) {
      event.preventDefault()
      this.remove()
    }
  }

  // Ticks one box, or with range every box from the last one ticked to this
  // one, all to the same state - so a Shift-click can untick a run too.
  _tick(box, checked, range) {
    const boxes = this._boxes()
    const from = range ? boxes.indexOf(this._anchor) : -1
    if (from === -1) {
      box.checked = checked
    } else {
      const to = boxes.indexOf(box)
      boxes.slice(Math.min(from, to), Math.max(from, to) + 1).forEach((each) => { each.checked = checked })
    }
    this._anchor = box
    this.changed()
  }

  _boxFor(row) {
    return row?.closest(".msg-swipe")?.querySelector(".msg-select-box")
  }

  _rowClick(event) {
    if (event.target.closest(".msg-select-bar")) return
    const label = event.target.closest(".msg-select")
    if (label) {
      const box = label.querySelector(".msg-select-box")
      // On the box itself the browser has already flipped it; on the rest of
      // the tile a Shift-click is taken over before the label forwards it.
      if (event.target === box) {
        if (event.shiftKey) this._tick(box, box.checked, true)
        else this._anchor = box
      } else if (event.shiftKey) {
        event.preventDefault()
        this._tick(box, !box.checked, true)
      }
      return
    }
    if (!this._selecting()) return
    const row = event.target.closest(".assistant-thread-item")
    if (!row) return
    // Anywhere else on a selectable row ticks it, instead of opening it; a row
    // that can't be ticked (an app, an in-app DM) does nothing until selecting ends.
    event.preventDefault()
    event.stopPropagation()
    const box = this._boxFor(row)
    if (!box) return
    this._tick(box, !box.checked, event.shiftKey)
  }

  // The filter toolbar sits outside this list (same turbo frame); the frame's
  // class hides it while the selection bar stands in for it.
  _setSelecting(on) {
    this.element.classList.toggle("is-selecting", on)
    this.element.closest("turbo-frame")?.classList.toggle("is-selecting-messages", on)
  }

  _selecting() {
    return this.element.classList.contains("is-selecting")
  }

  _boxes() {
    return [...this.element.querySelectorAll(".msg-select-box")]
  }

  _chosen() {
    return this._boxes().filter((box) => box.checked)
  }
}
